import React, { useMemo } from "react";
import { Lock } from "@mui/icons-material";
import { Box } from "@mui/material";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { useGetImportLayers } from "fond/hooks/useGetImportLayers";
import { ISGInputLayerIds, isImportInProgress, Project, WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AccordionStepper, AccordionStepperPane, NonIdealState, StackedNavigation } from "fond/widgets";

import Draw from "../DrawPanel";

import DataPrepPanel from "./DataPrep/DataPrepPanel";
import LeadInDrawPanel from "./DataPrep/LeadInDrawPanel";
import UGDrawPanel from "./DataPrep/UGDrawPanel";
import { InputPanel } from "./InputPanel";

const extendedDesignStackedNavigationPanels: Array<{ id: string; name: string; component: React.ReactNode; useDefaultHeader?: boolean }> = [
  // {
  //   id: "addresses",
  //   name: "Addresses",
  //   component: <InputMethodsPanel layerGroupId={LayerGroupIds.inAddress} />,
  // },
  // {
  //   id: "baseDataStreet",
  //   name: "Streets",
  //   component: <InputMethodsPanel layerGroupId={LayerGroupIds.inBaseDataStreet} />,
  // },
  // {
  //   id: "centralOffice",
  //   name: "Central Office",
  //   component: <InputMethodsPanel layerGroupId={LayerGroupIds.inExchange} />,
  // },
  // {
  //   id: "area-select",
  //   name: "Area Select",
  //   component: <AreaSelect />,
  //   useDefaultHeader: false,
  // },
  {
    id: "draw",
    name: "Draw on Map",
    component: <Draw />,
    useDefaultHeader: false,
  },
  // {
  //   id: "draw-polygon",
  //   name: "Draw Polygon",
  //   component: <PolygonSelect />,
  //   useDefaultHeader: false,
  // },
  // {
  //   id: "import-data",
  //   name: "Import data",
  //   component: <ImportData />,
  // },
  // {
  //   id: "use-existing-polygon",
  //   name: "Use existing polygon",
  //   component: <PolygonSelect />,
  //   useDefaultHeader: false,
  // },
  // {
  //   id: "dataPrepSettings",
  //   name: "Data Prep Settings",
  //   component: <DataPrepSettingsPanel />,
  // },
  {
    id: "modification-inLeadIns",
    name: "Data Prep - Lead-Ins",
    component: <LeadInDrawPanel />,
    useDefaultHeader: false,
  },
  {
    id: "modification-inUnderground",
    name: "Data Prep - Underground",
    component: <UGDrawPanel />,
    useDefaultHeader: false,
  },
];

interface PanelProps {
  project: Project;
  readOnly: boolean;
}

const Panel: React.FC<PanelProps> = ({ project, readOnly }: PanelProps) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const inputLayers = useGetImportLayers(versionId, Object.values(ISGInputLayerIds));
  const { workflowState: dataPrepWorkflow } = useCustomWorkflow({ versionId, key: WORKFLOW_KEYS.ISG.DATA_PREP_1 });

  const hasInProgressLayers = useMemo(() => {
    return Array.from(inputLayers.values()).some((layer) => layer.status && isImportInProgress(layer.status));
  }, [inputLayers]);

  const hasBoundary = !!inputLayers.get(ISGInputLayerIds.boundary)?.layer;
  const hasAddress = !!inputLayers.get(ISGInputLayerIds.address)?.layer;
  const allRequiredLayersUploaded = hasBoundary && hasAddress;

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Input data",
        id: "input",
        content: <InputPanel isReadOnly={readOnly} inputLayers={inputLayers} />,
        isComplete: allRequiredLayersUploaded && !hasInProgressLayers,
        isLoading: hasInProgressLayers,
      },
      {
        header: "Data preparation",
        id: "dataPrep",
        content: (
          <DataPrepPanel
            versionId={versionId}
            project={project}
            readOnly={readOnly}
            // canRun={allRequiredLayersUploaded}
          />
        ),
        isComplete: false, // TODO dataPrepWorkflow === "COMPLETE",
        isLoading: false, // sDataPrepInProgress,
      },
    ],
    [allRequiredLayersUploaded, hasInProgressLayers, inputLayers, project, readOnly, versionId]
  );

  if (!project) return null;

  return (
    <Box data-testid="isg-design-panel" sx={{ height: "100%" }}>
      {!permissionCheck(project.Permission?.Level, Actions.PROJECT_EDIT) ? (
        <NonIdealState size="small" icon={<Lock />} title="View only access" description="You only have view access to this project" />
      ) : (
        <StackedNavigation
          rootComponent={<AccordionStepper panes={accordionPanes} id={project.ID} />}
          screens={extendedDesignStackedNavigationPanels}
        />
      )}
    </Box>
  );
};

export default Panel;
